import React, { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';
import { playSound, SFX_KEYS } from '../game/systems/SoundManager';
import type {
  QuestDefinition,
  QuestProgress,
  QuestReward,
  PlayerQuestState,
  QuestUpdateEvent,
} from '../types/quests';
import { STARTER_VOYAGE_QUESTS, CAPTAIN_ALDRIC } from '../types/quests';

interface QuestTrackerPanelProps {
  socket: Socket | null;
  onClose: () => void;
  minimized: boolean;
  onToggleMinimize: () => void;
}

export const QuestTrackerPanel: React.FC<QuestTrackerPanelProps> = ({ 
  socket, 
  onClose, 
  minimized, 
  onToggleMinimize 
}) => {
  const [currentQuest, setCurrentQuest] = useState<QuestDefinition | null>(null);
  const [questProgress, setQuestProgress] = useState<QuestProgress | null>(null);
  const [starterVoyageComplete, setStarterVoyageComplete] = useState<boolean>(false);
  const [showRewardPopup, setShowRewardPopup] = useState<boolean>(false);
  const [completedQuest, setCompletedQuest] = useState<QuestDefinition | null>(null);
  const { handleHeaderMouseDown, style: dragStyle } = useDraggablePanel(0, 0);

  useEffect(() => {
    if (!socket) return;

    const handleFullState = (data: PlayerQuestState): void => {
      // Find the currently active quest
      const activeQuestId = Object.keys(data.quests).find(
        (id) => data.quests[id as keyof typeof data.quests].status === 'active'
      );

      if (activeQuestId) {
        const questDef = STARTER_VOYAGE_QUESTS.find((q) => q.id === activeQuestId);
        if (questDef) {
          setCurrentQuest(questDef);
          setQuestProgress(data.quests[activeQuestId as keyof typeof data.quests]);
        }
      }

      setStarterVoyageComplete(data.starterVoyageComplete);
    };

    const handleQuestUpdate = (data: QuestUpdateEvent): void => {
      setStarterVoyageComplete(data.starterVoyageComplete);

      // Update progress for the current active quest
      if (data.status === 'active' && data.questId === currentQuest?.id) {
        setQuestProgress({
          questId: data.questId,
          status: data.status,
          currentCount: data.currentCount,
          metadata: data.metadata,
        });
      }

      // Update for ANY quest that becomes active (new quest activation)
      if (data.status === 'active' && data.questId !== currentQuest?.id) {
        const questDef = STARTER_VOYAGE_QUESTS.find((q) => q.id === data.questId);
        if (questDef) {
          setCurrentQuest(questDef);
          setQuestProgress({
            questId: data.questId,
            status: data.status,
            currentCount: data.currentCount,
            metadata: data.metadata,
          });
        }
      }

      // Handle quest completion
      if (data.justCompleted && data.questId === currentQuest?.id) {
        const questDef = STARTER_VOYAGE_QUESTS.find((q) => q.id === data.questId);
        if (questDef) {
          setCompletedQuest(questDef);
          setShowRewardPopup(true);
          playSound(SFX_KEYS.QUEST_COMPLETE);
        }
      }
    };

    socket.on('quest:fullState', handleFullState);
    socket.on('quest:update', handleQuestUpdate);

    return () => {
      socket.off('quest:fullState', handleFullState);
      socket.off('quest:update', handleQuestUpdate);
    };
  }, [socket, currentQuest]);

  // Hide panel if starter voyage is complete or no active quest
  if (starterVoyageComplete || !questProgress || !currentQuest) {
    return null;
  }

  const progressPercent = Math.min(100, (questProgress.currentCount / currentQuest.targetCount) * 100);

  const getMentorDialogue = (): string => {
    if (questProgress.status === 'completed') {
      return currentQuest.mentorDialogue.complete;
    }
    if (questProgress.currentCount > 0) {
      return currentQuest.mentorDialogue.progress;
    }
    return currentQuest.mentorDialogue.start;
  };

  const formatRewards = (rewards: QuestReward[]): string => {
    return rewards
      .map((r) => {
        if (r.type === 'pieces') return `${r.amount?.toLocaleString()}₱`;
        if (r.type === 'item') return `${r.amount}x ${r.itemName}`;
        if (r.type === 'title') return `Title: "${r.title}"`;
        return '';
      })
      .filter(Boolean)
      .join(' + ');
  };

  if (minimized) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          background: 'rgba(13, 17, 23, 0.95)',
          border: '2px solid #30363d',
          padding: '8px 12px',
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          color: '#e2e8f0',
          cursor: 'pointer',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
        onClick={onToggleMinimize}
      >
        <span style={{ color: '#58a6ff' }}>[QUEST]</span>
        <span>{currentQuest.name}</span>
        <span style={{ color: '#8b949e' }}>
          {questProgress.currentCount}/{currentQuest.targetCount}
        </span>
        <span style={{ color: '#6b7280' }}>[+]</span>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          width: '320px',
          maxHeight: '80vh',
          background: 'rgba(13, 17, 23, 0.95)',
          border: '2px solid #30363d',
          fontFamily: 'var(--font-mono)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          ...dragStyle,
        }}
      >
        <div
          onMouseDown={handleHeaderMouseDown}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            background: '#161b22',
            borderBottom: '1px solid #30363d',
            cursor: 'grab',
            userSelect: 'none',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#58a6ff', fontSize: '10px', fontWeight: 'bold' }}>QUEST TRACKER</span>
            <span style={{ color: '#8b949e', fontSize: '10px' }}>
              {currentQuest.order + 1}/{STARTER_VOYAGE_QUESTS.length}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={onToggleMinimize}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8b949e',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '2px 6px',
              }}
            >
              [-]
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8b949e',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '2px 6px',
              }}
            >
              [X]
            </button>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '12px',
              padding: '12px',
              borderBottom: '1px solid #21262d',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                background: '#21262d',
                border: '2px solid #30363d',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={CAPTAIN_ALDRIC.portrait}
                alt={CAPTAIN_ALDRIC.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  imageRendering: 'pixelated',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = '<span style="color: #58a6ff; font-size: 24px;">[A]</span>';
                }}
              />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#58a6ff', fontSize: '12px', fontWeight: 'bold' }}>
                {CAPTAIN_ALDRIC.name}
              </div>
              <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '6px' }}>
                {CAPTAIN_ALDRIC.title}
              </div>
              <div
                style={{
                  color: '#c9d1d9',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '12px',
                }}
              >
                "{getMentorDialogue()}"
              </div>
            </div>
          </div>

          <div style={{ padding: '12px' }}>
            <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>
              {currentQuest.name}
            </div>

            <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>
              {currentQuest.objective}
            </div>

            {currentQuest.objectives && currentQuest.objectives.length > 0 ? (
              <div style={{ marginBottom: '8px' }}>
                {currentQuest.objectives.map((objective, idx) => {
                  const objectiveProgress = questProgress.metadata?.[objective.eventType] || 0;
                  const objectivePercent = Math.min(100, (objectiveProgress / objective.targetCount) * 100);
                  const objectiveLabel = objective.eventType
                    .replace(/_/g, ' ')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                  return (
                    <div key={idx} style={{ marginBottom: '8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '10px',
                          color: '#8b949e',
                          marginBottom: '2px',
                        }}
                      >
                        <span>{objectiveLabel}</span>
                        <span>
                          {objectiveProgress}/{objective.targetCount}
                        </span>
                      </div>
                      <div
                        style={{
                          height: '4px',
                          background: '#21262d',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            background: objectivePercent >= 100 ? '#238636' : '#58a6ff',
                            width: `${objectivePercent}%`,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ marginBottom: '8px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '10px',
                    color: '#8b949e',
                    marginBottom: '4px',
                  }}
                >
                  <span>Progress</span>
                  <span>
                    {questProgress.currentCount}/{currentQuest.targetCount}
                  </span>
                </div>
                <div
                  style={{
                    height: '6px',
                    background: '#21262d',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: progressPercent >= 100 ? '#238636' : '#58a6ff',
                      width: `${progressPercent}%`,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            )}

            <div
              style={{
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '8px',
                fontSize: '10px',
                color: '#8b949e',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#f0883e' }}>[HINT]</span> {currentQuest.hint}
            </div>

            <div style={{ fontSize: '10px' }}>
              <span style={{ color: '#8b949e' }}>Reward: </span>
              <span style={{ color: '#fbbf24' }}>{formatRewards(currentQuest.rewards)}</span>
            </div>
          </div>
        </div>
      </div>

      {showRewardPopup && completedQuest && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowRewardPopup(false)}
        >
          <div
            style={{
              background: '#0d1117',
              border: '2px solid #238636',
              padding: '24px',
              maxWidth: '400px',
              textAlign: 'center',
              fontFamily: 'var(--font-mono)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ color: '#238636', fontSize: '12px', marginBottom: '8px' }}>
              QUEST COMPLETE
            </div>
            <div style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
              {completedQuest.name}
            </div>
            <div className="font-lookout-14" style={{ color: '#c9d1d9', marginBottom: '16px' }}>
              "{completedQuest.mentorDialogue.complete}"
            </div>
            <div
              style={{
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '12px',
                marginBottom: '16px',
              }}
            >
              <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>REWARDS</div>
              <div style={{ color: '#fbbf24', fontSize: '14px' }}>
                {formatRewards(completedQuest.rewards)}
              </div>
            </div>
            <button
              onClick={() => setShowRewardPopup(false)}
              style={{
                background: '#238636',
                border: 'none',
                color: '#fff',
                padding: '8px 24px',
                cursor: 'pointer',
                fontSize: '12px',
                fontFamily: 'var(--font-mono)',
              }}
            >
              CONTINUE
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestTrackerPanel;

